import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { setEditImageModal } from '@redux/format-checker/formatChecker';
import { UiIcon } from '@components/common/icons/ui-icon';
import { useCodemirrorReferenceContext } from '@components/editor-page/change-content-context/codemirror-reference-context';
import { useApplicationState } from '@hooks/common/use-application-state';
import { useUiNotifications } from '@components/notifications/ui-notification-boundary';
import EditImageModal, { ImageFile } from './edit-image-modal';
import { useTranslation } from 'react-i18next';

export type ImageData = {
  imageDataTrigger: (imageFile: string) => void;
  singleImageObj: ImageFile;
  imageDataStatus: string;
  imageDataError: any;
  imageDataLoader: boolean;
  imageDataFetch: boolean;
};

export const EditImageButton = ({ imageData }: { imageData: ImageData }) => {
  const { editImageModalOpen } = useApplicationState(
    (state) => state.formatChecker
  );
  const { imageDataTrigger, singleImageObj, imageDataLoader, imageDataFetch } =
    imageData;
  const [uploadImage, setUploadImage] = useState<File | undefined>();
  const selection = useApplicationState((state) => state.noteDetails.selection);
  const [codeMirrorReference] = useCodemirrorReferenceContext();
  const { dispatchUiNotification } = useUiNotifications();
  const view = codeMirrorReference?.state;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const count = useMemo(
    () =>
      selection.to === undefined ? undefined : selection.to - selection.from,
    [selection.from, selection.to]
  );

  // These formats are intended for identifying selected image file types.
  const imageFormats = ['webp', 'png', 'jpeg', 'jpg'];
  const imageFormatsRegex = imageFormats.join('|');
  const handleImageEdit = useCallback(() => {
    if (selection.to !== undefined && selection.from !== undefined) {
      // get selected text from codemirror
      const selectedText = view?.sliceDoc(selection.from, selection.to);
      if (selectedText) {
        // check if selected text is an image url
        const regexPattern = new RegExp(
          `(https:\/\/[^\/]+\/(topics\/(media|images)\/[^\/]+\.(${imageFormatsRegex})))`,
          'i'
        );
        const isImageUrl = regexPattern.test(selectedText);
        if (isImageUrl) {
          // extract image url
          const imageUrl = regexPattern.exec(selectedText);
          // get image file name
          if (!imageUrl) {
            dispatchUiNotification(
              'Failed to get image url',
              'Please select a valid image url to edit',
              {}
            );
            return;
          }
          const imageFile = imageUrl[0].split('/').pop();
          imageDataTrigger(imageFile);
          dispatch(setEditImageModal(true));
        } else {
          dispatchUiNotification(
            'Failed to get image url',
            'Please select a valid image url to edit',
            {}
          );
        }
      }
    }
  }, [view, selection, dispatchUiNotification]);

  const handleImageEditClose = useCallback(() => {
    dispatch(setEditImageModal(false));
    setUploadImage(undefined);
  }, []);
  return (
    <>
      <Button
        disabled={!count || editImageModalOpen}
        title={t('editor.editorToolbar.editImage') ?? undefined}
        variant='light'
        onClick={handleImageEdit}>
        <UiIcon icon={PencilSquare} />
      </Button>
      <EditImageModal
        showEditImageModal={editImageModalOpen}
        handleEditClose={handleImageEditClose}
        uploadImage={uploadImage}
        setUploadImage={setUploadImage}
        imageFile={singleImageObj}
        isLoading={imageDataLoader || imageDataFetch}
      />
    </>
  );
};
